import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { CheckIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import { Organization, OrganizationLight } from "@/types/Organization";
import { cn } from "@/lib/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import Badge from "@components/Badge";

const height = 400;

export function OrganizationSwitcher({
  organizations,
  orgUuid,
}: {
  organizations: OrganizationLight[];
  orgUuid: string;
}) {
  const { t } = useLingui();

  const [filteredOrgs, setFilteredOrgs] =
    useState<OrganizationLight[]>(organizations);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [isKeyboardNavActive, setIsKeyboardNavActive] =
    useState<boolean>(false);

  const parentRef = useRef(null);

  // TanStack Virtual's useVirtualizer() returns non-memoizable functions, so
  // React Compiler skips optimizing this component. useWindowVirtualizer is
  // NOT a substitute here: CommandList is its own scroll container, and the
  // window variant would read viewport height and scroll offset off that div
  // (both undefined). Revisit when react-virtual ships a compatible API.
  // NOTE: disabling any react-hooks rule also makes the React Compiler skip
  // this component, so it gets no automatic memoization.
  // oxlint-disable-next-line react/incompatible-library
  const virtualizer = useVirtualizer({
    count: filteredOrgs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  });

  const virtualOrgs = virtualizer.getVirtualItems();

  function scrollToIndex(index: number) {
    virtualizer.scrollToIndex(index, {
      align: "center",
    });
  }

  function onSelectOption(uuid: Organization["uuid"]) {
    window.location.pathname = `/a/${uuid}/`;
  }

  function handleSearch(search: string) {
    setIsKeyboardNavActive(false);
    setFilteredOrgs(
      organizations.filter((org) =>
        org.name.toLowerCase().includes(search.toLowerCase() ?? [])
      )
    );
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    // Base UI's menu typeahead and list navigation cancel character and arrow
    // keys that reach the popup, so keep them inside the command list.
    if (event.key !== "Escape" && event.key !== "Tab") event.stopPropagation();

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        setFocusedIndex((prev) => {
          const newIndex =
            prev === -1 ? 0 : Math.min(prev + 1, filteredOrgs.length - 1);
          scrollToIndex(newIndex);
          return newIndex;
        });
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        setFocusedIndex((prev) => {
          const newIndex =
            prev === -1 ? filteredOrgs.length - 1 : Math.max(prev - 1, 0);
          scrollToIndex(newIndex);
          return newIndex;
        });
        break;
      }
      case "Enter": {
        event.preventDefault();
        if (filteredOrgs[focusedIndex]) {
          onSelectOption?.(filteredOrgs[focusedIndex].uuid);
        }
        break;
      }
      default:
        break;
    }
  }

  useEffect(() => {
    if (orgUuid) {
      const option = filteredOrgs.find((option) => option.uuid === orgUuid);
      if (option) {
        const index = filteredOrgs.indexOf(option);
        setFocusedIndex(index);
        virtualizer.scrollToIndex(index, {
          align: "center",
          behavior: "smooth",
        });
      }
    }
  }, [orgUuid, filteredOrgs, virtualizer]);

  return (
    <Command shouldFilter={false} onKeyDown={handleKeyDown}>
      <CommandInput
        data-testid="organization-picker-input"
        placeholder={t`Sök konto...`}
        className="h-9"
        autoFocus
        onValueChange={handleSearch}
      />
      <CommandList
        ref={parentRef}
        style={{
          height: height,
          width: "100%",
          overflow: "auto",
        }}
        onMouseDown={() => setIsKeyboardNavActive(false)}
        onMouseMove={() => setIsKeyboardNavActive(false)}
      >
        <CommandEmpty>
          <Trans>Inga företag hittades</Trans>
        </CommandEmpty>

        <CommandGroup>
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualOrgs.map((virtualOrg) => {
              const org = filteredOrgs[virtualOrg.index];

              return (
                <CommandItem
                  key={org.uuid}
                  disabled={isKeyboardNavActive}
                  className={cn(
                    "absolute left-0 top-0 w-full bg-transparent",
                    focusedIndex === virtualOrg.index &&
                      "bg-accent text-accent-foreground",
                    isKeyboardNavActive &&
                      focusedIndex !== virtualOrg.index &&
                      "aria-selected:bg-transparent aria-selected:text-primary"
                  )}
                  style={{
                    height: `${virtualOrg.size}px`,
                    transform: `translateY(${virtualOrg.start}px)`,
                  }}
                  value={org.uuid}
                  onMouseEnter={() =>
                    !isKeyboardNavActive && setFocusedIndex(virtualOrg.index)
                  }
                  onMouseLeave={() =>
                    !isKeyboardNavActive && setFocusedIndex(-1)
                  }
                  onSelect={onSelectOption}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      orgUuid === filteredOrgs[virtualOrg.index].uuid
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  <span className="truncate">
                    {filteredOrgs[virtualOrg.index].name}
                  </span>
                  <Badge className="ml-auto">
                    {filteredOrgs[virtualOrg.index].status}
                  </Badge>
                </CommandItem>
              );
            })}
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
